import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useUser } from 'src/utils/UserContext';
import { R2FThresholdTable } from '../../components/r2fThresholds/R2FThresholdTable';
import { useR2FThresholds } from '../../../core/hooks/useR2FThresholds';
import { AddHandlingTier } from '../../components/hierarchicalTable/AddHandlingTier';
import { AddProductionCapability } from '../../components/common/AddProductionCapability';
import { getValidWHTsForCountry } from '../../../types/WarehouseHandlingTier';
import { PendingThresholdChange } from 'src/features/types/r2fTypes';
import { useDefaultPCSafety } from '../../hooks/useDefaultPCSafety';
import SafetyWarning from '../../components/common/SafetyWarning';
import { useFCMetadata } from 'src/core/hooks/useGranularSLA';
import '../../components/r2fThresholds/styles.css';
import '../../components/common/styles.css';

interface LoadingProps {
    message: string;
}

// Placeholder for Loading component if it doesn't exist
const LoadingComponent: React.FC<LoadingProps> = ({ message }) => (
    <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{message}</p>
    </div>
);

interface ErrorProps {
    message: string;
}

// Placeholder for ErrorMessage component if it doesn't exist
const ErrorMessageComponent: React.FC<ErrorProps> = ({ message }) => (
    <div className="error-container">
        <div className="error-icon">⚠️</div>
        <p>{message}</p>
    </div>
);

/**
 * R2F Threshold Manager Page Component
 * 
 * This page allows users to view and manage R2F threshold configurations
 * for different warehouse and production capabilities.
 */
const R2FThresholdManager: React.FC = () => {
    // Get site/warehouse ID from URL parameters - using the siteId param name from navigationRoutes.ts
    const { siteId = '' } = useParams<{ siteId: string }>();
    // Use the site ID as warehouse ID for consistency with the route parameter
    const warehouseId = siteId;
    
    // Let the API handle the authenticated user
    const { user } = useUser();
    const hasEditPermission = user?.isAuthorizedForSLAOverrides === true;
    const [selectedWarehouse, setSelectedWarehouse] = useState<string>(warehouseId || 'DEFAULT');
    
    // Fetch FC metadata for country-based WHT filtering
    const {
        data: fcMetadata,
        isLoading: isFCMetadataLoading,
    } = useFCMetadata(selectedWarehouse);
    
    // Compute available tiers based on country code
    const countryBasedTiers = getValidWHTsForCountry(fcMetadata?.countryCode);
    
    // State for tracking pending changes
    const [pendingChanges, setPendingChanges] = useState<PendingThresholdChange[]>([]);
    
    // State to track custom PCs that need to be shown in the table
    const [pendingCustomPCs, setPendingCustomPCs] = useState<Set<string>>(new Set());
    
    // State for view-only mode - initialize to true if user doesn't have edit permissions
    const [viewOnlyMode, setViewOnlyMode] = useState<boolean>(!hasEditPermission);
    
    // Initialize safety hook for Default_PC protection
    const safety = useDefaultPCSafety({
        operationType: 'R2F',
        userAlias: user?.login,
        warehouseId: selectedWarehouse
    });
    
    // If user doesn't have SLA override permissions, force view-only mode
    useEffect(() => {
        if (!hasEditPermission) {
            setViewOnlyMode(true);
        }
    }, [hasEditPermission]);
    
    // Custom hook for R2F threshold data
    const { 
        data: originalData, 
        isLoading, 
        error, 
        saveThresholds, 
        userCanEdit: originalUserCanEdit,
        availableTiers,
        addProductionCapability: addPC,
        addCustomPC,
        deleteProductionCapability
    } = useR2FThresholds(
        selectedWarehouse, 
        user?.login, 
        viewOnlyMode ? false : true, // Force userCanEdit to false in view-only mode
        !viewOnlyMode // preserveDefaultTypes is false in view-only mode
    );
    
    // Explicitly calculate the effective userCanEdit value
    const userCanEdit = viewOnlyMode ? false : originalUserCanEdit;
    
    // Function to toggle view-only mode
    const toggleViewMode = () => {
        setViewOnlyMode(prev => !prev);
    };
    
    // Enhance the data with pending custom PCs
    const data = useMemo(() => {
        if (!originalData || !pendingCustomPCs.size) return originalData;
        
        // Create a deep copy of the original data
        const enhancedData = {
            ...originalData,
            productionCapabilities: [...originalData.productionCapabilities]
        };
        
        // Add synthetic production capabilities for each pending custom PC
        Array.from(pendingCustomPCs).forEach(pcName => {
            // Check if this PC already exists in the data
            const existingPC = enhancedData.productionCapabilities.find(pc => pc.name === pcName);
            if (existingPC) return; // Skip if it already exists
            
            // Create a synthetic ID for this PC
            const syntheticId = `pending_${pcName.toLowerCase().replace(/\s+/g, '_')}`;
            
            // Find any pending changes for this PC to determine what tiers it has
            const pcChanges = pendingChanges.filter(change => 
                change.productionCapability === pcName && change.action === 'Add'
            );
            
            // Add this as a new production capability - note: we don't auto-create a default node
            enhancedData.productionCapabilities.push({
                id: syntheticId,
                name: pcName,
                defaultNode: undefined, // No default node initially
                additionalNodes: [], // Will be populated from pending changes in R2FThresholdTable
                isNetworkDefault: false,
                isFCDefault: false,
                hasDefaultNode: false // Explicitly mark that there's no default node
            });
            
            // Synthetic production capability added
        });
        
        return enhancedData;
    }, [originalData, pendingCustomPCs, pendingChanges]);

    // Handler for adding new custom Production Capabilities as pending changes
    const handleAddCustomPC = async (pcName: string, warehouseHandlingTier: string): Promise<void> => {
        const newChanges: PendingThresholdChange[] = [];
        
        // Create a change for the specified handling tier
        const isDefault = warehouseHandlingTier === 'DEFAULT';
        
        const tierChange: PendingThresholdChange = {
            productionCapability: pcName,
            handlingTier: warehouseHandlingTier, // Use the exact tier name
            lowThreshold: 0,
            highThreshold: 100,
            userAlias: user?.login || '',
            reason: `Create ${warehouseHandlingTier} tier for PC ${pcName}`,
            action: 'Add',
            isDefault: isDefault
        };
        
        newChanges.push(tierChange);
        
        // Add all changes to the pending changes list
        setPendingChanges(prev => [...prev, ...newChanges]);
        
    // Add to the set of pending custom PCs
    setPendingCustomPCs(prev => new Set([...Array.from(prev), pcName]));
};

// Handler for removing pending (unsaved) PCs
const handleRemovePendingPC = (pcName: string) => {
    // Remove from pendingCustomPCs
    setPendingCustomPCs(prev => {
        const newSet = new Set(prev);
        newSet.delete(pcName);
        return newSet;
    });
    
    // Remove all pending changes for this PC
    setPendingChanges(prev => prev.filter(change => change.productionCapability !== pcName));
};
    
    // Handle saving all changes, including custom PCs and other modifications
    const handleSaveAll = async (tableChanges: PendingThresholdChange[]) => {
        // Combine the custom PC changes with any table changes
        const allChanges = [...pendingChanges, ...tableChanges];
        
        // CRITICAL SAFETY CHECK: Validate for Default_PC entries
        const safetyCheck = safety.validateSafety(allChanges);
        
        if (safetyCheck.shouldBlock) {
            // Block the save operation and show error message
            safety.showWarning(safetyCheck.errorMessage, 'error');
            
            // Log the safety violation for audit purposes
            safety.logViolation(safetyCheck.defaultPCCount);
            
            // Do not proceed with the save
            return;
        }
        
        // Call the API with all changes (already validated to not contain Default_PC)
        await saveThresholds(allChanges);
        
        // Clear the pending changes and custom PCs after successful save
        setPendingChanges([]);
        setPendingCustomPCs(new Set());
    };
    
    // Display loading state while fetching data
    if (isLoading || isFCMetadataLoading) {
        return <LoadingComponent message="Loading R2F threshold configurations..." />;
    }
    
    // Display error if data fetching failed
    if (error) {
        return <ErrorMessageComponent message={error} />;
    }
    
    // Get available handling tiers to use as production capabilities
    const handleAddProductionCapability = async (pcId: string, handlingTier: string) => {
        // Instead of making an immediate API call, add as a pending change
        try {
            // Create pending changes for the new PC with default thresholds
            const newChanges: PendingThresholdChange[] = [{
                productionCapability: handlingTier, // Using the handling tier as the PC name
                handlingTier: 'DEFAULT',
                lowThreshold: 0,
                highThreshold: 100,
                userAlias: user?.login || '',
                reason: `Create new production capability ${handlingTier}`,
                action: 'Add',
                isDefault: true
            }];
            
            // Add to pending changes
            setPendingChanges(prev => [...prev, ...newChanges]);
            
            // Add to pending custom PCs to ensure it shows in the UI
            setPendingCustomPCs(prev => new Set([...Array.from(prev), handlingTier]));
            
            return true;
        } catch (err) {
            console.error('Failed to add production capability thresholds:', err);
            return false;
        }
    };
    
    // Display empty state if no data is available
    if (!data || data.productionCapabilities.length === 0) {
        return (
            <div className="r2f-threshold-manager">
                <h1>TRB Threshold Configuration</h1>
                <div className="empty-state">
                    <p>No TRB threshold configurations found for this warehouse.</p>
                    {userCanEdit && (
                        <div className="r2f-add-production-capability">
                            <h3>Select Production Capability</h3>
                            <p>Add TRB threshold at warehouse handling tier for production capability:</p>
                            <table className="r2f-add-table">
                                <tbody>
                                    <AddHandlingTier
                                        pcId="production-capabilities" // placeholder ID since we don't have production capabilities here
                                        availableTiers={countryBasedTiers}
                                        onAdd={handleAddProductionCapability}
                                        userCanEdit={userCanEdit}
                                    />
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        );
    }
    
    return (
        <div className="r2f-threshold-manager">
            <h1>TRB Threshold Configuration</h1>
            
            {/* Safety Warning Display */}
            {safety.warningState?.isVisible && (
                <SafetyWarning
                    message={safety.warningState.message}
                    type={safety.warningState.type}
                    operationType={safety.warningState.operationType}
                    onDismiss={safety.dismissWarning}
                />
            )}
            
            <div className="r2f-description">
                <p>
                    Configure low and high Tiny-Red-Button (TRB) thresholds for different production capabilities and nodes.
                    These thresholds determine when inventory is considered ready for fulfillment.
                </p>
                <p className="inheritance-note">
                    <strong>Note:</strong> Values shown represent the effective thresholds for this warehouse. 
                    Some values may be inherited from network-wide default settings when not explicitly configured for this warehouse.
                </p>
            </div>
            
            {/* Only show toggle if user has edit permissions */}
            {hasEditPermission && (
                <div className="view-mode-toggle">
                    <label className="toggle-switch">
                        <input 
                            type="checkbox" 
                            checked={viewOnlyMode} 
                            onChange={toggleViewMode} 
                        />
                        <span className="toggle-label">View-Only Mode</span>
                        {viewOnlyMode && <span className="toggle-note">(Default types hidden)</span>}
                    </label>
                </div>
            )}
            
            <div className="warehouse-selector">
                <h3>
                    Warehouse: {selectedWarehouse === 'DEFAULT' 
                        ? 'DEFAULT (Network-Wide Settings)' 
                        : selectedWarehouse}
                </h3>
                {/* Warehouse selector would go here in a real implementation */}
            </div>

            {userCanEdit && (
                <>
                    <div className="add-pc-section">
                        <h3>Add Override for Additional Production Capability</h3>
                        <AddProductionCapability
                            existingPCs={data.productionCapabilities.map(pc => pc.name)}
                            onAdd={handleAddCustomPC}
                            userCanEdit={userCanEdit}
                            availableTiers={countryBasedTiers}
                            
                            // New API integration props
                            warehouseIds={[selectedWarehouse]}
                            enableAPIMode={true}
                            fallbackToTextInput={true}
                            showErrorDetails={false}
                            
                            // UI customization for R2F context
                            dropdownPlaceholder="Select a Production Capability for R2F thresholds"
                            textInputPlaceholder="Enter custom Production Capability name"
                            loadingMessage="Loading production capabilities..."
                            
                            // Event callbacks for better user experience
                            onAPIError={(error) => {
                                console.warn('Failed to load production capabilities from API for R2F:', error.message);
                            }}
                            onModeSwitch={(mode) => {
                                console.log(`R2F AddProductionCapability switched to ${mode} mode`);
                            }}
                        />
                    </div>
                    <hr className="divider" />
                </>
            )}
            
            <R2FThresholdTable
                data={data}
                onSave={handleSaveAll}
                onDelete={(productionCapabilityId, productionCapability, reason, actionSource) => {
                    // Process deletion request
                    return deleteProductionCapability(productionCapability, reason, actionSource);
                }}
                onRemovePendingPC={handleRemovePendingPC}
                userCanEdit={userCanEdit}
                availableTiers={countryBasedTiers}
                pendingChanges={pendingChanges}
                warehouseId={selectedWarehouse}
            />
            
            {pendingChanges.length > 0 && (
                <div className="pending-changes-info">
                    <p>You have {pendingChanges.length} pending change(s) that will be applied when you click Save.</p>
                </div>
            )}
        </div>
    );
};

export default R2FThresholdManager;
